import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PanelTemplate from "../../../templates/Panel";
import Box from "../../../components/Box";
import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import InputSelect from "../../../components/InputSelect";
import Toggle from "../../../components/Toggle";
import Avatar from "../../../components/Avatar";
import Icon from "../../../components/Icon";
import SelectAdmin from "../../../components/selects/SelectAdmin";
import api from "../../../services/api";
import { toast } from "react-toastify";
import { renderError } from "../../../helpers/errors";
import { hasPermission } from "../../../services/auth";
import { createOptions } from "../../../helpers/selects";
import { generateUniqueId } from "../../../helpers/helpers";
import { loadEmojiPickerData } from "../../../helpers/emoji";
import { convertToMoney } from "../../../helpers/masks";
import { convertMoenyToInt } from "../../../helpers/conversions";
import { listAutomations } from "../../../services/automations";
import HelpTooltip from "../../../components/HelpTooltip";
import PipelineDeleteModal from "./PipelineDeleteModal";
import { RuleSentence } from "../Automations/RuleSentence";
import {
    DndContext,
    PointerSensor,
    closestCenter,
    useSensor,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    useSortable,
    horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    TRIGGERS,
    ACTIONS,
    actionForTrigger,
    triggerOptions,
    triggerGroupTitle,
    originOptions,
    amountOperatorOptions,
    taskTypeOptions,
    taskTypeLabel,
    taskDueOptions,
    cardModeOptions,
    cardModeLabel,
    responsibleModeOptions,
    CONDITION_TYPES,
} from "../../../seeds/crm/automations";

// Sensor de drag que só inicia o arraste na alça (elemento dentro de ".drag"),
// para não atrapalhar o clique nos campos da etapa.
class StageDragSensor extends PointerSensor {
    static activators = [
        {
            eventName: "onPointerDown",
            handler: ({ nativeEvent: event }) => {
                if (!event.isPrimary || event.button !== 0) return false;
                return !!event.target?.closest?.(".drag");
            },
        },
    ];
}

const newStage = () => ({
    id: `previd-${generateUniqueId()}`,
    name: "",
    max_days_in_stage: "3",
});

// Tipos de status disponíveis para os cards do painel. A escolha vale para
// todos os cards: em painéis comerciais o card pode terminar ganho ou perdido;
// em painéis operacionais ele só fica ativo ou arquivado.
const CARD_STATUS_MODES = [
    {
        value: "commercial",
        title: "Com resultado comercial",
        description: "Para processos de venda ou negociação, nos quais a oportunidade pode ser concluída como ganha ou perdida.",
        statuses: [
            { label: "Aberto", tone: "open" },
            { label: "Ganho", tone: "won" },
            { label: "Perdido", tone: "lost" },
            { label: "Arquivado", tone: "archived" },
        ],
    },
    {
        value: "operational",
        title: "Sem resultado comercial",
        description: "Para processos operacionais ou administrativos, nos quais o card apenas permanece ativo ou é arquivado.",
        statuses: [
            { label: "Aberto", tone: "open" },
            { label: "Arquivado", tone: "archived" },
        ],
    },
];

// Como os cards podem ser criados no painel (allow_manual_card_creation).
// Em painéis "somente por automações" o botão de criar card não é exibido no
// board e o backend rejeita a criação manual.
const CARD_CREATION_MODES = [
    {
        value: true,
        title: "Criação manual e automática",
        description: "Os usuários poderão criar cards manualmente, e o sistema também poderá gerar cards por meio de automações.",
    },
    {
        value: false,
        title: "Somente por automações",
        description: "Os cards deste painel serão gerados exclusivamente pelo sistema. A opção de criar card manualmente não será exibida.",
    },
];

// Etapas do backend vêm com id/order; normaliza para o estado local (ordenadas).
const normalizeStages = (stages = []) =>
    [...stages]
        .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
        .map((s) => ({
            id: s.id,
            name: s.name ?? "",
            max_days_in_stage: String(s.max_days_in_stage ?? "3"),
        }));

// Automação do backend -> forma local (a etapa é referenciada pelo id local).
const toLocalAutomation = (a) => ({
    id: a.id,
    trigger: a.trigger,
    stage_local_id: a.pipeline_stage_id,
    conditions: Array.isArray(a.conditions) ? a.conditions : null,
    action_config: a.action_config ?? null,
    is_active: a.is_active ?? true,
});

const triggerIcon = (trigger) =>
    triggerOptions.find((t) => t.value === trigger)?.icon ?? "⚡";

// Descrição curta, em linguagem do usuário, do que a automação de um modelo
// faz — é o argumento de venda do modelo, então falamos do efeito, não da
// configuração.
const templateAutomationText = (automation) => {
    if (automation.trigger === TRIGGERS.BUDGET_CREATED) {
        return "Cria um card sozinho a cada novo orçamento que chegar";
    }
    if (automation.trigger === TRIGGERS.ORDER_CREATED) {
        return `Cria ${cardModeLabel(automation.action_config?.card_creation_mode)} sozinho a cada novo pedido`;
    }
    if (automation.trigger === TRIGGERS.CARD_ENTERED_STAGE) {
        return `Cria uma tarefa de ${taskTypeLabel(automation.action_config?.task_type).toLowerCase()} quando o card entrar na etapa`;
    }
    return "Automação incluída";
};

/**
 * Coluna de etapa no mini-board: o formulário tem a cara do painel real.
 * Arraste horizontal pela alça para reordenar.
 */
const SortableStageColumn = ({ stage, index, total, automationCount, updateStageParam, deleteStage }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stage.id });
    const style = { transform: CSS.Transform.toString(transform), transition };

    return (
        <div
            className={`pipeline-stage-col ${isDragging ? "is-dragging" : ""}`}
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
        >
            <div className="pipeline-stage-col__bar">
                <span className="pipeline-stage-col__order">{index + 1}ª etapa</span>
                <span className="pipeline-stage-col__bar-tools">
                    <span className="pipeline-stage-col__handle drag" title="Arraste para reordenar">
                        <Icon name="drag" size={16} />
                    </span>
                    <button
                        type="button"
                        className="pipeline-stage-col__delete"
                        title={total <= 1 ? "O painel precisa de ao menos uma etapa" : "Excluir etapa"}
                        aria-label="Excluir etapa"
                        disabled={total <= 1}
                        onClick={() => deleteStage(stage.id)}
                    >
                        <Icon name="delete-small" size={15} />
                    </button>
                </span>
            </div>
            <div className="pipeline-stage-col__fields">
                <Input label={`Nome da etapa`} value={stage.name} change={(value) => updateStageParam(stage.id, "name", value)} />
                <Input
                    label={`Limite de dias`}
                    value={stage.max_days_in_stage}
                    change={(value) => updateStageParam(stage.id, "max_days_in_stage", value)}
                    mask={`convertToInt`}
                    right={<HelpTooltip text={`Prazo máximo (em dias) que um card pode ficar nesta etapa. Ao ultrapassá-lo, o card é destacado em vermelho no funil, sinalizando que está atrasado.`} />}
                />
            </div>
            {automationCount > 0 &&
                <span
                    className="pipeline-stage-col__auto"
                    title={`${automationCount} automação(ões) criam cards ou tarefas nesta etapa`}
                >
                    <Icon name="zap" size={12} /> {automationCount} automação{automationCount === 1 ? "" : "s"}
                </span>
            }
        </div>
    );
};

/**
 * Escolha de ponto de partida (só na criação): um modelo pronto ou em branco.
 * O cartão vende o modelo pelo que a automação faz por quem usa — as etapas
 * ficam para depois, no formulário, para não desanimar a escolha.
 */
const TemplateChooser = ({ templates, selectedKey, onPick, onBlank, onBack, showBack }) => (
    <section className="pipeline-chooser">
        <div className="pipeline-chooser__intro">
            <h2>Como você quer começar?</h2>
            <p>Modelos já trazem etapas e automações prontas. Tudo pode ser ajustado depois.</p>
        </div>
        <div className="pipeline-chooser__grid">
            {templates.map((template) => (
                <button
                    key={template.key}
                    type="button"
                    className={`pipeline-template-card ${selectedKey === template.key ? "is-selected" : ""}`}
                    onClick={() => onPick(template)}
                >
                    <span className="pipeline-template-card__head">
                        <span className="pipeline-template-card__icon">{template.icon}</span>
                        <span className="pipeline-template-card__name">{template.name}</span>
                    </span>
                    <span className="pipeline-template-card__desc">{template.description}</span>
                    {(template.automations ?? []).length > 0 &&
                        <span className="pipeline-template-card__autos">
                            {template.automations.map((automation, i) => (
                                <span className="pipeline-template-card__auto" key={i}>
                                    <Icon name="zap" size={13} />
                                    <span>{templateAutomationText(automation)}</span>
                                </span>
                            ))}
                        </span>
                    }
                </button>
            ))}
            <button
                type="button"
                className={`pipeline-template-card pipeline-template-card--blank ${selectedKey === "blank" ? "is-selected" : ""}`}
                onClick={onBlank}
            >
                <span className="pipeline-template-card__head">
                    <span className="pipeline-template-card__icon"><Icon name="plus-circle" size={22} /></span>
                    <span className="pipeline-template-card__name">Começar em branco</span>
                </span>
                <span className="pipeline-template-card__desc">Monte as etapas e as automações do seu jeito, do zero.</span>
            </button>
        </div>
        {showBack &&
            <button type="button" className="pipeline-chooser__back" onClick={onBack}>
                ← Voltar para a edição sem trocar o modelo
            </button>
        }
    </section>
);

/**
 * Editor de uma automação, restrito ao painel em criação/edição: a coluna é
 * escolhida entre as etapas locais (por id local), funcionando mesmo com etapas
 * ainda não salvas. Organizado como a regra é lida: Quando → Se → Então,
 * com a frase-resumo sempre visível no topo.
 */
const AutomationEditorModal = ({ show, close, onSubmit, automation, stages }) => {
    const isEdit = !!automation;
    const [trigger, setTrigger] = useState(automation?.trigger ?? TRIGGERS.BUDGET_CREATED);
    const [stageLocalId, setStageLocalId] = useState(automation?.stage_local_id ?? null);
    const [conditions, setConditions] = useState([]);
    const [cardMode, setCardMode] = useState("per_product");
    const [responsibleMode, setResponsibleMode] = useState("order_admin");
    const [responsibleAdminId, setResponsibleAdminId] = useState(null);
    const [taskType, setTaskType] = useState("call");
    const [taskTitle, setTaskTitle] = useState("");
    const [taskDue, setTaskDue] = useState(0);

    useEffect(() => {
        if (!show) return;
        const cfg = automation?.action_config ?? {};
        setTrigger(automation?.trigger ?? TRIGGERS.BUDGET_CREATED);
        setStageLocalId(automation?.stage_local_id ?? null);
        setConditions(Array.isArray(automation?.conditions) ? automation.conditions.map((c) => ({ ...c })) : []);
        setCardMode(cfg.card_creation_mode ?? "per_product");
        setResponsibleMode(cfg.card_responsible_mode ?? "order_admin");
        setResponsibleAdminId(cfg.card_responsible_admin_id ?? null);
        setTaskType(cfg.task_type ?? "call");
        setTaskTitle(cfg.task_title ?? "");
        setTaskDue(cfg.task_due_offset_days ?? 0);
    }, [show, automation]);

    const action = actionForTrigger(trigger);
    const stageOptions = useMemo(() => createOptions(stages, "name", "id"), [stages]);

    const availableConditionTypes = useMemo(() => {
        if (trigger === TRIGGERS.BUDGET_CREATED) return [CONDITION_TYPES.ORIGIN, CONDITION_TYPES.AMOUNT];
        if (trigger === TRIGGERS.ORDER_CREATED) return [CONDITION_TYPES.AMOUNT];
        return [];
    }, [trigger]);
    const usedConditionTypes = conditions.map((c) => c.type);
    const addableTypes = availableConditionTypes.filter((t) => !usedConditionTypes.includes(t));

    const addCondition = (type) => {
        const base = type === CONDITION_TYPES.AMOUNT ? { type, operator: "gte", value: 0 } : { type, value: "site" };
        setConditions((prev) => [...prev, base]);
    };
    const updateCondition = (index, patch) => setConditions((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
    const removeCondition = (index) => setConditions((prev) => prev.filter((_, i) => i !== index));

    const handleTriggerChange = (value) => {
        setTrigger(value || TRIGGERS.BUDGET_CREATED);
        setConditions([]);
    };

    const stageName = stages.find((s) => String(s.id) === String(stageLocalId))?.name;
    const previewRule = {
        trigger,
        action,
        conditions,
        action_config:
            action === ACTIONS.CREATE_TASK
                ? { task_type: taskType, task_title: taskTitle, task_due_offset_days: taskDue }
                : { card_creation_mode: cardMode, card_responsible_mode: responsibleMode, card_responsible_admin_id: responsibleAdminId },
        pipeline: { name: "este painel" },
        stage: { name: stageName ?? "coluna" },
    };

    const valid =
        !!stageLocalId &&
        (action !== ACTIONS.CREATE_TASK || !!taskType) &&
        !(trigger === TRIGGERS.ORDER_CREATED && responsibleMode === "fixed_admin" && !responsibleAdminId);

    const buildLocal = () => {
        const local = {
            id: automation?.id ?? null,
            trigger,
            stage_local_id: stageLocalId,
            conditions: conditions.length ? conditions : null,
            is_active: automation?.is_active ?? true,
        };
        if (action === ACTIONS.CREATE_TASK) {
            local.action_config = { task_type: taskType, task_title: taskTitle || null, task_due_offset_days: Number(taskDue) };
        } else if (trigger === TRIGGERS.ORDER_CREATED) {
            local.action_config = {
                card_creation_mode: cardMode,
                card_responsible_mode: responsibleMode,
                card_responsible_admin_id: responsibleMode === "fixed_admin" ? responsibleAdminId : null,
            };
        } else {
            local.action_config = null;
        }
        return local;
    };

    const handleSubmit = () => {
        if (!valid) return;
        onSubmit(buildLocal());
        close();
    };

    return (
        <Modal
            className="pipeline-rule-editor"
            title={isEdit ? "Editar automação" : "Nova automação"}
            show={show}
            close={close}
            footer={
                <Button type="primary" size="small" svg="save-white" text="Salvar automação" action={handleSubmit} disabled={!valid} />
            }
        >
            <div className="rule-editor">
                <div className="rule-editor__preview" aria-live="polite">
                    <span className="rule-editor__preview-label">Resumo da regra</span>
                    <RuleSentence rule={previewRule} />
                </div>

                <div className="rule-editor__step">
                    <h4 className="rule-editor__step-title"><em>1</em> Quando acontecer…</h4>
                    {isEdit ? (
                        <div className="rule-editor__trigger-fixed">
                            <span className="rule-editor__trigger-fixed-icon">{triggerIcon(trigger)}</span>
                            <span>{triggerOptions.find((t) => t.value === trigger)?.title ?? trigger}</span>
                        </div>
                    ) : (
                        <div className="rule-editor__triggers">
                            {triggerOptions.map((t) => (
                                <button
                                    key={t.value}
                                    type="button"
                                    className={`rule-editor__trigger-card ${trigger === t.value ? "is-selected" : ""}`}
                                    onClick={() => handleTriggerChange(t.value)}
                                >
                                    <span className="rule-editor__trigger-icon">{t.icon}</span>
                                    <span className="rule-editor__trigger-title">{t.title}</span>
                                    <span className="rule-editor__trigger-desc">{t.description}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {availableConditionTypes.length > 0 &&
                    <div className="rule-editor__step">
                        <h4 className="rule-editor__step-title"><em>2</em> Se… <span className="rule-editor__step-optional">(opcional)</span></h4>
                        {conditions.length === 0 &&
                            <p className="rule-editor__hint">Sem condições, a regra vale para todos os casos.</p>
                        }
                        {conditions.map((cond, index) => (
                            <div className="rule-editor__condition" key={`${cond.type}-${index}`}>
                                {cond.type === CONDITION_TYPES.ORIGIN ? (
                                    <div className="rule-editor__condition-field">
                                        <InputSelect label="Origem" value={cond.value} options={originOptions} change={(value) => updateCondition(index, { value })} />
                                    </div>
                                ) : (
                                    <>
                                        <div className="rule-editor__condition-field">
                                            <InputSelect label="Valor" value={cond.operator} options={amountOperatorOptions} change={(value) => updateCondition(index, { operator: value })} />
                                        </div>
                                        <div className="rule-editor__condition-field">
                                            <Input label="Valor" value={convertToMoney(String(cond.value ?? 0))} change={(value) => updateCondition(index, { value: convertMoenyToInt(value) })} />
                                        </div>
                                    </>
                                )}
                                <button type="button" className="rule-editor__remove" aria-label="Remover condição" onClick={() => removeCondition(index)}>×</button>
                            </div>
                        ))}
                        {addableTypes.length > 0 &&
                            <div className="rule-editor__add-conditions">
                                {addableTypes.includes(CONDITION_TYPES.ORIGIN) &&
                                    <Button type="link" size="small" text="+ Condição por origem" action={() => addCondition(CONDITION_TYPES.ORIGIN)} />
                                }
                                {addableTypes.includes(CONDITION_TYPES.AMOUNT) &&
                                    <Button type="link" size="small" text="+ Condição por valor" action={() => addCondition(CONDITION_TYPES.AMOUNT)} />
                                }
                            </div>
                        }
                    </div>
                }

                <div className="rule-editor__step">
                    <h4 className="rule-editor__step-title">
                        <em>{availableConditionTypes.length > 0 ? 3 : 2}</em>{" "}
                        {action === ACTIONS.CREATE_TASK ? "Então criar a tarefa…" : "Então criar o card…"}
                    </h4>
                    <div className="row">
                        <div className="col-12">
                            <InputSelect label="Na coluna" value={stageLocalId} options={stageOptions} change={(value) => setStageLocalId(value || null)} />
                        </div>
                    </div>

                    {trigger === TRIGGERS.ORDER_CREATED &&
                        <div className="row">
                            <div className="col-6">
                                <InputSelect label="Como criar o card" value={cardMode} options={cardModeOptions} change={(value) => setCardMode(value || "per_product")} />
                            </div>
                            <div className="col-6">
                                <InputSelect
                                    label="Responsável"
                                    value={responsibleMode}
                                    options={responsibleModeOptions}
                                    change={(value) => {
                                        setResponsibleMode(value || "order_admin");
                                        if (value !== "fixed_admin") setResponsibleAdminId(null);
                                    }}
                                />
                            </div>
                        </div>
                    }
                    {trigger === TRIGGERS.ORDER_CREATED && responsibleMode === "fixed_admin" &&
                        <div className="row"><div className="col-12"><SelectAdmin selectedAdmin={responsibleAdminId} setSelectedAdmin={setResponsibleAdminId} /></div></div>
                    }

                    {action === ACTIONS.CREATE_TASK &&
                        <>
                            <div className="row">
                                <div className="col-6"><InputSelect label="Tipo de tarefa" value={taskType} options={taskTypeOptions} change={(value) => setTaskType(value || "call")} /></div>
                                <div className="col-6"><InputSelect label="Prazo" value={taskDue} options={taskDueOptions} change={(value) => setTaskDue(value ?? 0)} /></div>
                            </div>
                            <div className="row"><div className="col-12"><Input label="Título da tarefa (opcional)" value={taskTitle} change={setTaskTitle} placeholder="Ex.: Ligar para confirmar o prazo" /></div></div>
                        </>
                    }
                </div>
            </div>
        </Modal>
    );
};

/**
 * Página de criação/edição de painel (pipeline).
 *
 * Fluxo pensado para reduzir carga cognitiva:
 *  - Criação começa com UMA decisão (escolher modelo ou em branco); o
 *    formulário só aparece depois, já preenchido.
 *  - Identidade do painel (emoji + nome) vira o "título do documento".
 *  - As etapas são editadas como colunas de um mini-board — a mesma forma
 *    que terão no funil real (com contagem de automações por coluna).
 *  - Automações e acesso vêm depois, em seções de largura cheia, agrupadas
 *    e pesquisáveis.
 *
 * Rotas: /crm/painel/novo (criação) e /crm/painel/:id/editar (edição).
 */
export const PipelineFormPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isNew = !id;

    const canManageMembers = hasPermission("manage_pipelines");
    const canEditAutomations = hasPermission("edit_crm_settings");

    const [loading, setLoading] = useState(!isNew);
    const [view, setView] = useState(isNew ? "chooser" : "form");
    const [name, setName] = useState("");
    const [icon, setIcon] = useState(null);
    const [cardStatusMode, setCardStatusMode] = useState("commercial");
    const [allowManualCardCreation, setAllowManualCardCreation] = useState(true);
    const [stages, setStages] = useState(isNew ? [newStage()] : []);
    const [automations, setAutomations] = useState([]);
    const [selectedAdminIds, setSelectedAdminIds] = useState([]);
    const [allAdmins, setAllAdmins] = useState([]);
    const [members, setMembers] = useState([]); // [{id, name, open_cards_count, ...}]
    const [memberQuery, setMemberQuery] = useState("");

    const [templates, setTemplates] = useState([]);
    const [selectedTemplateKey, setSelectedTemplateKey] = useState(null);

    const [saving, setSaving] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [emojiPicker, setEmojiPicker] = useState(null);

    const [editorState, setEditorState] = useState({ show: false, index: null });

    const sensors = [useSensor(StageDragSensor)];

    // Carrega dados: templates (criação); painel + automações + membros (edição);
    // e a lista de admins para os toggles de acesso.
    useEffect(() => {
        if (isNew) {
            api.get("/pipeline-templates")
                .then((res) => setTemplates(res.data?.resources ?? []))
                .catch(() => {});
        } else {
            api.get(`/pipeline/${id}`)
                .then((res) => {
                    const pipeline = res.data?.resource;
                    if (!pipeline) {
                        toast.error("Painel não encontrado");
                        navigate("/crm", { replace: true });
                        return;
                    }
                    setName(pipeline.name ?? "");
                    setIcon(pipeline.icon ?? null);
                    setCardStatusMode(pipeline.card_status_mode ?? "commercial");
                    setAllowManualCardCreation(Boolean(pipeline.allow_manual_card_creation ?? true));
                    setStages(normalizeStages(pipeline.stages));
                })
                .catch((error) => {
                    renderError(error);
                    navigate("/crm", { replace: true });
                })
                .finally(() => setLoading(false));

            if (canEditAutomations) {
                listAutomations({ pipeline_id: id })
                    .then((res) => setAutomations((res.data?.resources ?? []).map(toLocalAutomation)))
                    .catch((error) => renderError(error));
            }
            api.get(`/pipeline/${id}/admins`)
                .then((res) => {
                    const list = res.data?.resources ?? [];
                    setMembers(list);
                    setSelectedAdminIds(list.map((m) => m.id));
                })
                .catch((error) => renderError(error));
        }

        if (canManageMembers) {
            api.get("/list")
                .then((res) => {
                    const raw = res.data?.resources ?? res.data?.data ?? [];
                    setAllAdmins(Array.isArray(raw) ? raw : []);
                })
                .catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openEmojiPicker = () => {
        if (!emojiPicker) {
            Promise.all([import("@emoji-mart/react"), loadEmojiPickerData()])
                .then(([pickerMod, data]) => setEmojiPicker({ Picker: pickerMod.default, data }))
                .catch(() => toast.error("Não foi possível carregar os emojis"));
        }
        setShowEmojiPicker(true);
    };

    const applyTemplate = (template) => {
        setSelectedTemplateKey(template.key);
        setName((current) => (current.trim() ? current : template.name));
        setIcon(template.icon ?? null);
        setCardStatusMode(template.card_status_mode ?? "commercial");
        setAllowManualCardCreation(Boolean(template.allow_manual_card_creation ?? true));
        const mappedStages = (template.stages ?? []).map((s) => ({
            id: `previd-${generateUniqueId()}`,
            name: s.name,
            max_days_in_stage: String(s.max_days_in_stage ?? "3"),
        }));
        setStages(mappedStages.length ? mappedStages : [newStage()]);
        setAutomations(
            (template.automations ?? [])
                .map((a) => {
                    const stage = mappedStages[a.stage_index];
                    if (!stage) return null;
                    return {
                        id: null,
                        trigger: a.trigger,
                        stage_local_id: stage.id,
                        conditions: a.conditions ?? null,
                        action_config: a.action_config ?? null,
                        is_active: true,
                    };
                })
                .filter(Boolean)
        );
        setView("form");
    };

    const startBlank = () => {
        setSelectedTemplateKey("blank");
        setName("");
        setIcon(null);
        setCardStatusMode("commercial");
        setAllowManualCardCreation(true);
        setStages([newStage()]);
        setAutomations([]);
        setView("form");
    };

    // Etapas
    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        setStages((prev) => {
            const oldIndex = prev.findIndex((s) => s.id === active.id);
            const newIndex = prev.findIndex((s) => s.id === over.id);
            if (oldIndex < 0 || newIndex < 0) return prev;
            return arrayMove(prev, oldIndex, newIndex);
        });
    };
    const addStage = () => setStages((prev) => [...prev, newStage()]);
    const deleteStage = (stageId) => {
        setStages((prev) => prev.filter((s) => s.id !== stageId));
        // Automações que apontavam para essa etapa perdem o destino.
        setAutomations((prev) => prev.filter((a) => String(a.stage_local_id) !== String(stageId)));
    };
    const updateStageParam = (stageId, param, value) =>
        setStages((prev) => prev.map((s) => (s.id === stageId ? { ...s, [param]: value } : s)));

    const automationCountByStage = useMemo(() => {
        const map = {};
        automations.forEach((a) => {
            const key = String(a.stage_local_id);
            map[key] = (map[key] ?? 0) + 1;
        });
        return map;
    }, [automations]);

    // Automações
    const openAutomationEditor = (index = null) => setEditorState({ show: true, index });
    const closeAutomationEditor = () => setEditorState({ show: false, index: null });
    const submitAutomation = (local) => {
        setAutomations((prev) => {
            if (editorState.index === null) return [...prev, local];
            return prev.map((a, i) => (i === editorState.index ? local : a));
        });
    };
    const toggleAutomation = (index) =>
        setAutomations((prev) => prev.map((a, i) => (i === index ? { ...a, is_active: !a.is_active } : a)));
    const removeAutomation = (index) => setAutomations((prev) => prev.filter((_, i) => i !== index));

    // Regras agrupadas pelo gatilho (na ordem de leitura), guardando o índice
    // original para editar/pausar/excluir.
    const automationGroups = useMemo(() => {
        const order = [TRIGGERS.BUDGET_CREATED, TRIGGERS.ORDER_CREATED, TRIGGERS.CARD_ENTERED_STAGE];
        return order
            .map((trigger) => ({
                trigger,
                items: automations
                    .map((automation, index) => ({ automation, index }))
                    .filter(({ automation }) => automation.trigger === trigger),
            }))
            .filter((group) => group.items.length > 0);
    }, [automations]);

    // Usuários
    const toggleAdmin = (adminId) =>
        setSelectedAdminIds((prev) => (prev.includes(adminId) ? prev.filter((x) => x !== adminId) : [...prev, adminId]));

    const memberRows = useMemo(() => {
        const base = allAdmins.length > 0 ? allAdmins : members;
        return [...base].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    }, [allAdmins, members]);

    const filteredMemberRows = useMemo(() => {
        const query = memberQuery.trim().toLowerCase();
        if (!query) return memberRows;
        return memberRows.filter((row) =>
            `${row.name ?? ""} ${row.email ?? ""}`.toLowerCase().includes(query)
        );
    }, [memberRows, memberQuery]);

    const openCardsById = useMemo(() => {
        const map = {};
        members.forEach((m) => { map[m.id] = m.open_cards_count ?? 0; });
        return map;
    }, [members]);

    const removedWithOpenCards = useMemo(
        () => members.filter((m) => !selectedAdminIds.includes(m.id) && (m.open_cards_count ?? 0) > 0),
        [members, selectedAdminIds]
    );

    const buildPayload = () => {
        const payload = {
            name,
            icon,
            card_status_mode: cardStatusMode,
            allow_manual_card_creation: allowManualCardCreation,
            stages: stages.map((s) => ({
                id: String(s.id).startsWith("previd") ? null : s.id,
                name: s.name,
                max_days_in_stage: s.max_days_in_stage,
            })),
        };
        if (canManageMembers) {
            payload.admins = selectedAdminIds;
        }
        if (canEditAutomations) {
            payload.automations = automations
                .map((a) => ({ ...a, stage_index: stages.findIndex((s) => String(s.id) === String(a.stage_local_id)) }))
                .filter((a) => a.stage_index >= 0)
                .map((a) => ({
                    id: a.id ?? null,
                    trigger: a.trigger,
                    stage_index: a.stage_index,
                    conditions: a.conditions ?? null,
                    action_config: a.action_config ?? null,
                    is_active: a.is_active ?? true,
                }));
        }
        return payload;
    };

    const emptyStageNames = stages.some((s) => !s.name.trim());
    const canSave = name.trim().length > 0 && stages.length > 0 && !emptyStageNames;
    const saveHint = !name.trim()
        ? "Dê um nome ao painel."
        : stages.length === 0
            ? "Adicione ao menos uma etapa."
            : emptyStageNames
                ? "Todas as etapas precisam de nome."
                : null;

    const goBack = () => navigate("/crm");

    const save = () => {
        if (!canSave) return;
        setSaving(true);
        const payload = buildPayload();
        const request = isNew ? api.post("/pipeline", payload) : api.put(`/pipeline/${id}`, payload);
        request
            .then((res) => {
                const savedId = res?.data?.resource?.id ?? id;
                toast.success(isNew ? "Painel criado com sucesso" : "Painel atualizado com sucesso");
                if (savedId) {
                    navigate(`/crm/painel/${savedId}`, { replace: true });
                } else {
                    navigate("/crm", { replace: true });
                }
            })
            .catch((error) => renderError(error))
            .finally(() => setSaving(false));
    };

    const editingAutomation = editorState.index !== null ? automations[editorState.index] : null;

    return (
        <PanelTemplate id="crm-pipeline-form">
            <div className="pipeline-form">
                <header className="pipeline-form__header">
                    <button type="button" className="pipeline-form__back" onClick={goBack}>
                        <span className="pipeline-form__back-arrow" aria-hidden="true">←</span>
                        <span>Painéis</span>
                    </button>
                    <h1 className="pipeline-form__title">{isNew ? "Criar painel" : "Editar painel"}</h1>
                </header>

                {loading &&
                    <p className="pipeline-form__loading">Carregando…</p>
                }

                {!loading && view === "chooser" &&
                    <TemplateChooser
                        templates={templates}
                        selectedKey={selectedTemplateKey}
                        onPick={applyTemplate}
                        onBlank={startBlank}
                        onBack={() => setView("form")}
                        showBack={selectedTemplateKey !== null}
                    />
                }

                {!loading && view === "form" &&
                    <>
                        <section className="pipeline-hero">
                            <div className="pipeline-hero__identity">
                                <div className="pipeline-emoji">
                                    <button
                                        type="button"
                                        className="pipeline-emoji__trigger"
                                        onClick={() => (showEmojiPicker ? setShowEmojiPicker(false) : openEmojiPicker())}
                                        aria-label="Escolher emoji do painel"
                                        title="Escolher emoji do painel"
                                    >
                                        {icon || (name || "").trim().charAt(0).toUpperCase() || "🙂"}
                                    </button>
                                    {showEmojiPicker &&
                                        <>
                                            <div className="pipeline-emoji__overlay" onClick={() => setShowEmojiPicker(false)} />
                                            <div className="pipeline-emoji__popover">
                                                {emojiPicker
                                                    ? <emojiPicker.Picker
                                                        data={emojiPicker.data}
                                                        locale="pt"
                                                        theme="light"
                                                        previewPosition="none"
                                                        skinTonePosition="none"
                                                        onEmojiSelect={(emoji) => { setIcon(emoji.native); setShowEmojiPicker(false); }}
                                                    />
                                                    : <div className="pipeline-emoji__loading">Carregando…</div>
                                                }
                                            </div>
                                        </>
                                    }
                                </div>
                                <div className="pipeline-hero__name">
                                    <Input label={`Nome do painel`} value={name} change={setName} />
                                </div>
                            </div>
                        </section>

                        <Box
                            className="pipeline-stages-box"
                            title="Etapas do funil"
                            tools={<Button type="link" size="small" text="+ Nova etapa" action={addStage} />}
                        >
                            <p className="pipeline-section-hint">
                                Cada etapa vira uma coluna do painel, na ordem abaixo. Arraste pela alça <Icon name="drag" size={13} /> para reordenar.
                            </p>
                            <div className="pipeline-stages__strip">
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <SortableContext items={stages.map((s) => s.id)} strategy={horizontalListSortingStrategy}>
                                        {stages.map((stage, index) => (
                                            <SortableStageColumn
                                                key={stage.id}
                                                stage={stage}
                                                index={index}
                                                total={stages.length}
                                                automationCount={automationCountByStage[String(stage.id)] ?? 0}
                                                updateStageParam={updateStageParam}
                                                deleteStage={deleteStage}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                                <button type="button" className="pipeline-stages__add" onClick={addStage}>
                                    <Icon name="plus-circle" size={20} />
                                    <span>Nova etapa</span>
                                </button>
                            </div>
                        </Box>

                        <Box className="pipeline-status-mode" title="Tipos de status disponíveis para os cards">
                            <p className="pipeline-section-hint">
                                Define como os cards deste painel podem ser concluídos. A escolha vale para todos os cards do painel.
                            </p>
                            <div className="pipeline-status-mode__grid" role="radiogroup" aria-label="Tipos de status disponíveis para os cards">
                                {CARD_STATUS_MODES.map((mode) => {
                                    const selected = cardStatusMode === mode.value;
                                    return (
                                        <button
                                            key={mode.value}
                                            type="button"
                                            role="radio"
                                            aria-checked={selected}
                                            className={`pipeline-status-mode__card ${selected ? "is-selected" : ""}`}
                                            onClick={() => setCardStatusMode(mode.value)}
                                        >
                                            <span className="pipeline-status-mode__head">
                                                <span className="pipeline-status-mode__radio" aria-hidden="true" />
                                                <span className="pipeline-status-mode__title">{mode.title}</span>
                                            </span>
                                            <span className="pipeline-status-mode__desc">{mode.description}</span>
                                            <span className="pipeline-status-mode__statuses">
                                                <span className="pipeline-status-mode__statuses-label">Status disponíveis</span>
                                                <span className="pipeline-status-mode__chips">
                                                    {mode.statuses.map((status) => (
                                                        <span key={status.tone} className={`pipeline-status-chip pipeline-status-chip--${status.tone}`}>
                                                            {status.label}
                                                        </span>
                                                    ))}
                                                </span>
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </Box>

                        <Box className="pipeline-status-mode" title="Como os cards serão criados neste painel?">
                            <p className="pipeline-section-hint">
                                Define se os usuários podem criar cards manualmente ou se eles são gerados apenas pelas automações do painel.
                            </p>
                            <div className="pipeline-status-mode__grid" role="radiogroup" aria-label="Como os cards serão criados neste painel?">
                                {CARD_CREATION_MODES.map((mode) => {
                                    const selected = allowManualCardCreation === mode.value;
                                    return (
                                        <button
                                            key={String(mode.value)}
                                            type="button"
                                            role="radio"
                                            aria-checked={selected}
                                            className={`pipeline-status-mode__card ${selected ? "is-selected" : ""}`}
                                            onClick={() => setAllowManualCardCreation(mode.value)}
                                        >
                                            <span className="pipeline-status-mode__head">
                                                <span className="pipeline-status-mode__radio" aria-hidden="true" />
                                                <span className="pipeline-status-mode__title">{mode.title}</span>
                                            </span>
                                            <span className="pipeline-status-mode__desc">{mode.description}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </Box>

                        {canEditAutomations &&
                            <Box
                                className="pipeline-automations"
                                title="Automações"
                                tools={
                                    <Button
                                        type="secondary"
                                        size="small"
                                        svg="zap"
                                        text="Nova automação"
                                        action={() => openAutomationEditor(null)}
                                        disabled={stages.length === 0}
                                    />
                                }
                            >
                                <p className="pipeline-section-hint">Regras que criam cards ou tarefas automaticamente neste painel.</p>
                                {automations.length === 0 &&
                                    <div className="pipeline-automations__empty">
                                        <span className="pipeline-automations__empty-icon"><Icon name="zap" size={22} /></span>
                                        <p>Nenhuma automação ainda. Crie uma para o painel trabalhar sozinho — por exemplo, criar um card a cada novo orçamento.</p>
                                        <Button type="link" size="small" text="+ Criar a primeira automação" action={() => openAutomationEditor(null)} disabled={stages.length === 0} />
                                    </div>
                                }
                                {automationGroups.map((group) => (
                                    <div className="pipeline-automations__group" key={group.trigger}>
                                        <h4 className="pipeline-automations__group-title">
                                            <span aria-hidden="true">{triggerIcon(group.trigger)}</span>
                                            {triggerGroupTitle(group.trigger)}
                                        </h4>
                                        {group.items.map(({ automation, index }) => {
                                            const stageName = stages.find((s) => String(s.id) === String(automation.stage_local_id))?.name;
                                            const previewRule = {
                                                trigger: automation.trigger,
                                                action: actionForTrigger(automation.trigger),
                                                conditions: automation.conditions,
                                                action_config: automation.action_config,
                                                pipeline: { name: name || "este painel" },
                                                stage: { name: stageName ?? "coluna" },
                                            };
                                            return (
                                                <div className={`rule-card ${automation.is_active ? "" : "rule-card--paused"}`} key={automation.id ?? `new-${index}`}>
                                                    <button type="button" className="rule-card__sentence" onClick={() => openAutomationEditor(index)} title="Clique para editar">
                                                        <RuleSentence rule={previewRule} />
                                                        {!automation.is_active && <span className="rule-card__paused">Pausada</span>}
                                                    </button>
                                                    <div className="rule-card__actions" onClick={(e) => e.stopPropagation()}>
                                                        <Toggle checked={automation.is_active} onChange={() => toggleAutomation(index)} label={automation.is_active ? "Pausar automação" : "Ativar automação"} />
                                                        <Button type={`delete`} svg={`delete-small`} action={() => removeAutomation(index)} />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}
                            </Box>
                        }

                        {canManageMembers &&
                            <Box
                                className="pipeline-members"
                                title="Quem pode acessar"
                                tools={
                                    <span className="pipeline-members__count">
                                        {selectedAdminIds.length} de {memberRows.length} com acesso
                                    </span>
                                }
                            >
                                <p className="pipeline-section-hint">Quem pode ver e trabalhar neste painel. Você entra automaticamente.</p>
                                {memberRows.length > 5 &&
                                    <div className="pipeline-members__search">
                                        <Input label="Buscar pessoa" value={memberQuery} change={setMemberQuery} />
                                    </div>
                                }
                                <ul className="pipeline-members__list">
                                    {filteredMemberRows.length === 0 &&
                                        <li className="pipeline-members__none">Ninguém encontrado com “{memberQuery}”.</li>
                                    }
                                    {filteredMemberRows.map((row) => {
                                        const checked = selectedAdminIds.includes(row.id);
                                        const openCards = openCardsById[row.id] ?? 0;
                                        return (
                                            <li key={row.id} className="pipeline-members__row">
                                                <div className="pipeline-members__info">
                                                    <Avatar name={row.name} photo={row.photo_url} size={32} />
                                                    <div className="pipeline-members__text">
                                                        <span className="pipeline-members__name">{row.name}</span>
                                                        <span className="pipeline-members__email">{row.email}{row.role_name ? ` · ${row.role_name}` : ""}</span>
                                                    </div>
                                                </div>
                                                <div className="pipeline-members__meta">
                                                    {openCards > 0 &&
                                                        <span className="pipeline-members__badge" title="Cards em aberto atribuídos a este usuário neste painel">
                                                            {openCards} card{openCards === 1 ? "" : "s"} em aberto
                                                        </span>
                                                    }
                                                    <Toggle checked={checked} onChange={() => toggleAdmin(row.id)} label={`Acesso de ${row.name}`} />
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                                {removedWithOpenCards.length > 0 &&
                                    <div className="pipeline-members__warning">
                                        <strong>Atenção:</strong> ao salvar, {removedWithOpenCards.length === 1 ? "este usuário deixará" : "estes usuários deixarão"} de ver o painel, incluindo cards abertos que ainda são responsabilidade {removedWithOpenCards.length === 1 ? "dele" : "deles"}. Os cards permanecem; reatribua-os se necessário.
                                    </div>
                                }
                            </Box>
                        }
                    </>
                }

                {!loading && view === "form" &&
                    <footer className="pipeline-form__footer">
                        <div className="pipeline-form__footer-inner">
                            <div className="pipeline-form__footer-left">
                                {!isNew &&
                                    <Button type={`secondary`} size={`small`} svg={`delete-small`} text={`Excluir painel`} action={() => setShowDelete(true)} />
                                }
                            </div>
                            <div className="pipeline-form__footer-right">
                                {saveHint && <span className="pipeline-form__hint">{saveHint}</span>}
                                <Button type={`secondary`} size={`small`} text={`Cancelar`} action={goBack} />
                                <Button type={`primary`} size={`small`} svg={`save-white`} text={isNew ? "Criar painel" : "Salvar alterações"} action={save} loading={saving} disabled={!canSave} />
                            </div>
                        </div>
                    </footer>
                }
            </div>

            {editorState.show &&
                <AutomationEditorModal
                    show={true}
                    close={closeAutomationEditor}
                    onSubmit={submitAutomation}
                    automation={editingAutomation}
                    stages={stages}
                />
            }

            {showDelete &&
                <PipelineDeleteModal
                    pipeline={{ id, name }}
                    close={() => setShowDelete(false)}
                    onDeleted={() => navigate("/crm", { replace: true })}
                />
            }
        </PanelTemplate>
    );
};

export default PipelineFormPage;
